import { ApiRef, createApiRef } from '@backstage/core-plugin-api';
import type {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  AssetExplorerListResponse,
  AssetExplorerObjectMetadata,
  AssetExplorerPresignRequest,
  AssetExplorerPresignResult,
} from '../types';

export interface ListObjectsOptions {
  bucket: string;
  prefix?: string;
  continuationToken?: string;
  pageSize?: number;
}

export interface PresignObjectOptions {
  bucket: string;
  key: string;
  request: AssetExplorerPresignRequest;
}

export interface GetMetadataOptions {
  bucket: string;
  key: string;
}

/**
 * API interface for the Asset Explorer plugin.
 * @public
 */
export interface AssetExplorerApi {
  listObjects(options: ListObjectsOptions): Promise<AssetExplorerListResponse>;
  getObjectMetadata(
    options: GetMetadataOptions,
  ): Promise<AssetExplorerObjectMetadata>;
  generatePresignedUrl(
    options: PresignObjectOptions,
  ): Promise<AssetExplorerPresignResult>;
}

/**
 * API reference for the Asset Explorer plugin.
 * @public
 */
export const assetExplorerApiRef: ApiRef<AssetExplorerApi> =
  createApiRef<AssetExplorerApi>({
    id: 'plugin.asset-explorer.service',
  });

/**
 * Default implementation of the Asset Explorer API.
 * @public
 */
export class AssetExplorerClient implements AssetExplorerApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
    private readonly identityApi: IdentityApi,
  ) {}

  async listObjects(
    options: ListObjectsOptions,
  ): Promise<AssetExplorerListResponse> {
    const { bucket, prefix, continuationToken, pageSize } = options;
    const searchParams = new URLSearchParams();
    if (prefix) {
      searchParams.set('prefix', prefix);
    }
    if (continuationToken) {
      searchParams.set('continuationToken', continuationToken);
    }
    if (pageSize) {
      searchParams.set('pageSize', String(pageSize));
    }
    const query = searchParams.toString();
    const querySuffix = query ? `?${query}` : '';
    const path = `/v1/buckets/${encodeURIComponent(
      bucket,
    )}/objects${querySuffix}`;
    return this.request<AssetExplorerListResponse>(path);
  }

  async getObjectMetadata(
    options: GetMetadataOptions,
  ): Promise<AssetExplorerObjectMetadata> {
    const { bucket, key } = options;
    const path = `/v1/buckets/${encodeURIComponent(
      bucket,
    )}/objects/${encodeURIComponent(key)}/head`;
    return this.request<AssetExplorerObjectMetadata>(path);
  }

  async generatePresignedUrl(
    options: PresignObjectOptions,
  ): Promise<AssetExplorerPresignResult> {
    const { bucket, key, request } = options;
    const path = `/v1/buckets/${encodeURIComponent(
      bucket,
    )}/objects/${encodeURIComponent(key)}:presign`;
    return this.request<AssetExplorerPresignResult>(path, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('asset-explorer');
    const identity = await this.identityApi.getBackstageIdentity();
    const userId = identity.userEntityRef.split(':').pop()?.split('/').pop();

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(userId && { 'x-userid': userId }),
        ...(init?.headers as Record<string, string>),
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    if (response.status === 204) {
      return undefined as unknown as T;
    }

    return (await response.json()) as T;
  }
}
